/**
 * Knock out Printify studio white backgrounds on homepage / catalog mood tiles
 * (collections + spotlight). Runs deferred + one-at-a-time so header hover stays live.
 */
(function () {
  const cache = new Map();
  const queue = [];
  let active = 0;
  const CONCURRENCY = 2;
  const MAX_EDGE_DEFAULT = 720;
  const MAX_EDGE_THUMB = 360;
  const FLOOD_LUMA = 228;
  const FRINGE_LUMA = 210;
  const FRINGE_PASSES = 2;

  function isMobileViewport() {
    try {
      return (
        window.matchMedia('(max-width: 989px)').matches ||
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches
      );
    } catch (e) {
      return false;
    }
  }

  const MOBILE = isMobileViewport();

  /** Show tile immediately without canvas (mobile main-thread freeze / opacity:0 trap). */
  function revealAsIs(photo) {
    if (!photo || !(photo instanceof HTMLImageElement)) return;
    const src = photo.dataset.knockSrc || photo.currentSrc || photo.getAttribute('src');
    if (src && !String(src).startsWith('data:') && photo.getAttribute('src') !== src) {
      photo.setAttribute('src', src);
    }
    photo.classList.add('is-knocked');
    delete photo.dataset.knocking;
    delete photo.dataset.knockRevealing;
  }

  function luma(r, g, b) {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function isHomeKnockTarget(photo) {
    return Boolean(
      photo.closest('.auracase-collections, .auracase-spotlight') ||
        photo.classList.contains('auracase-collections__phone-img') ||
        photo.classList.contains('auracase-spotlight__phone-img')
    );
  }

  function maxEdgeFor(photo) {
    if (photo && photo.classList.contains('auracase-collections__phone-img')) return MAX_EDGE_THUMB;
    return MAX_EDGE_DEFAULT;
  }

  function clearWhiteBackground(img, options) {
    const nocrop = Boolean(options && options.nocrop);
    const profile = (options && options.profile) || 'default';
    const leather = profile === 'leather';
    const maxEdge = (options && options.maxEdge) || MAX_EDGE_DEFAULT;

    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    if (!nw || !nh) return null;

    const scale = Math.min(1, maxEdge / Math.max(nw, nh));
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);

    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, w, h);
    } catch (err) {
      return null;
    }

    const data = imageData.data;
    const n = w * h;

    function chroma(r, g, b) {
      return Math.max(r, g, b) - Math.min(r, g, b);
    }

    const fringePasses = leather ? 6 : FRINGE_PASSES;
    const fringeLuma = leather ? 175 : FRINGE_LUMA;
    const fringeChroma = leather ? 36 : 28;
    const haloPasses = leather ? 4 : 0;
    const haloLuma = 130;
    const haloChroma = 40;
    const floodLuma = FLOOD_LUMA;
    const floodChroma = 18;

    const mask = new Uint8Array(n);
    const stack = [];

    function isStudioWhite(r, g, b, a) {
      if (a < 10) return true;
      const y = luma(r, g, b);
      if (y < floodLuma) return false;
      return chroma(r, g, b) <= floodChroma;
    }

    function isFringeAt(r, g, b, a, minLuma, maxChroma) {
      if (a < 10) return false;
      const y = luma(r, g, b);
      if (y < minLuma) return false;
      return chroma(r, g, b) <= maxChroma;
    }

    function touchesDark(i) {
      const x = i % w;
      const y = (i / w) | 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const no = (ny * w + nx) * 4;
          if (data[no + 3] < 10) continue;
          if (luma(data[no], data[no + 1], data[no + 2]) < 90) return true;
        }
      }
      return false;
    }

    function tryPush(x, y) {
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const i = y * w + x;
      if (mask[i]) return;
      const o = i * 4;
      if (!isStudioWhite(data[o], data[o + 1], data[o + 2], data[o + 3])) return;
      if (!leather && touchesDark(i)) return;
      mask[i] = 1;
      stack.push(i);
    }

    for (let x = 0; x < w; x++) {
      tryPush(x, 0);
      tryPush(x, h - 1);
    }
    for (let y = 0; y < h; y++) {
      tryPush(0, y);
      tryPush(w - 1, y);
    }

    while (stack.length) {
      const i = stack.pop();
      const x = i % w;
      const y = (i / w) | 0;
      tryPush(x + 1, y);
      tryPush(x - 1, y);
      tryPush(x, y + 1);
      tryPush(x, y - 1);
    }

    function growMask(passes, minLuma, maxChroma, diagonals) {
      for (let pass = 0; pass < passes; pass++) {
        const grow = [];
        for (let i = 0; i < n; i++) {
          if (mask[i]) continue;
          const o = i * 4;
          if (!isFringeAt(data[o], data[o + 1], data[o + 2], data[o + 3], minLuma, maxChroma)) continue;
          const x = i % w;
          const y = (i / w) | 0;
          let touches = false;
          if (x > 0 && mask[i - 1]) touches = true;
          else if (x + 1 < w && mask[i + 1]) touches = true;
          else if (y > 0 && mask[i - w]) touches = true;
          else if (y + 1 < h && mask[i + w]) touches = true;
          else if (diagonals) {
            if (x > 0 && y > 0 && mask[i - w - 1]) touches = true;
            else if (x + 1 < w && y > 0 && mask[i - w + 1]) touches = true;
            else if (x > 0 && y + 1 < h && mask[i + w - 1]) touches = true;
            else if (x + 1 < w && y + 1 < h && mask[i + w + 1]) touches = true;
          }
          if (touches) grow.push(i);
        }
        for (let g = 0; g < grow.length; g++) mask[grow[g]] = 1;
      }
    }

    growMask(fringePasses, fringeLuma, fringeChroma, leather);
    if (haloPasses) growMask(haloPasses, haloLuma, haloChroma, true);

    let minX = w;
    let minY = h;
    let maxX = -1;
    let maxY = -1;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (mask[i]) {
        data[o + 3] = 0;
        continue;
      }

      const r = data[o];
      const g = data[o + 1];
      const b = data[o + 2];
      const a = data[o + 3];
      const x = i % w;
      const y = (i / w) | 0;

      let border = 0;
      if (x > 0 && mask[i - 1]) border += 1;
      if (x + 1 < w && mask[i + 1]) border += 1;
      if (y > 0 && mask[i - w]) border += 1;
      if (y + 1 < h && mask[i + w]) border += 1;

      if (border > 0) {
        const yL = luma(r, g, b);
        const c = chroma(r, g, b);
        if (leather && c <= 42 && yL > 120) {
          const t = Math.min(1, Math.max(0, (yL - 120) / 80));
          const strength = 0.8 + 0.18 * Math.min(1, border / 2);
          data[o + 3] = Math.round(a * (1 - t * strength));
          data[o] = Math.round(r * (1 - t * 0.5));
          data[o + 1] = Math.round(g * (1 - t * 0.5));
          data[o + 2] = Math.round(b * (1 - t * 0.5));
        } else if (yL > 190) {
          const t = Math.min(1, Math.max(0, (yL - 190) / 50));
          data[o + 3] = Math.round(a * (1 - t * 0.92));
          data[o] = Math.round(r - (255 - r) * t * 0.35);
          data[o + 1] = Math.round(g - (255 - g) * t * 0.35);
          data[o + 2] = Math.round(b - (255 - b) * t * 0.35);
        }
      }

      if (data[o + 3] < 8) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    ctx.putImageData(imageData, 0, 0);

    const exportCanvas = (() => {
      if (nocrop || maxX < 0) return canvas;

      const padX = Math.max(2, Math.round((maxX - minX + 1) * 0.012));
      const padY = Math.max(2, Math.round((maxY - minY + 1) * 0.012));
      minX = Math.max(0, minX - padX);
      minY = Math.max(0, minY - padY);
      maxX = Math.min(w - 1, maxX + padX);
      maxY = Math.min(h - 1, maxY + padY);

      const cw = maxX - minX + 1;
      const ch = maxY - minY + 1;
      const cropped = document.createElement('canvas');
      cropped.width = cw;
      cropped.height = ch;
      const cctx = cropped.getContext('2d');
      if (!cctx) return canvas;
      cctx.drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
      return cropped;
    })();

    return canvasToUrl(exportCanvas);
  }

  function canvasToUrl(canvas) {
    return new Promise((resolve) => {
      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(canvas.toDataURL('image/png'));
              return;
            }
            resolve(URL.createObjectURL(blob));
          },
          'image/png'
        );
        return;
      }
      resolve(canvas.toDataURL('image/png'));
    });
  }

  function reveal(photo, cleared) {
    if (!cleared) {
      delete photo.dataset.knocking;
      return;
    }

    photo.dataset.knockRevealing = '1';

    const finishShow = () => {
      photo.classList.add('is-knocked');
      delete photo.dataset.knocking;
      requestAnimationFrame(() => {
        delete photo.dataset.knockRevealing;
      });
    };

    const isData = String(cleared).startsWith('data:') || String(cleared).startsWith('blob:');
    if (isData) {
      photo.classList.remove('is-knock-fallback');
      photo.removeAttribute('srcset');
      photo.removeAttribute('sizes');
      if (photo.getAttribute('src') !== cleared) {
        photo.setAttribute('src', cleared);
      }
      finishShow();
      return;
    }

    /* JPEG fallback — still mark knocked so tiles aren't blank; CSS multiply hides white on dark */
    photo.classList.add('is-knock-fallback');
    if (photo.getAttribute('src') !== cleared) {
      photo.setAttribute('src', cleared);
    }
    finishShow();
  }

  function enqueue(photo, priority) {
    if (!photo || !(photo instanceof HTMLImageElement)) return;
    if (!isHomeKnockTarget(photo)) return;
    if (photo.classList.contains('is-knocked') && /^(data:|blob:)/.test(String(photo.getAttribute('src') || ''))) {
      return;
    }
    if (queue.includes(photo)) return;
    if (priority) queue.unshift(photo);
    else queue.push(photo);
    drainQueue();
  }

  function drainQueue() {
    while (active < CONCURRENCY && queue.length) {
      const photo = queue.shift();
      active += 1;
      processPhotoAsync(photo)
        .catch((err) => {
          console.warn('[knock-white] failed', err);
          const fallback = photo.dataset.knockSrc || photo.currentSrc || photo.getAttribute('src');
          if (fallback && !String(fallback).startsWith('data:')) reveal(photo, fallback);
          else photo.classList.add('is-knocked');
          delete photo.dataset.knocking;
        })
        .finally(() => {
          active -= 1;
          /* Long yield so header hover / scroll stay responsive between jobs */
          setTimeout(drainQueue, 56);
        });
    }
  }

  function processPhotoAsync(photo) {
    const src = photo.dataset.knockSrc || photo.currentSrc || photo.getAttribute('src');
    if (!src || src.startsWith('data:')) {
      photo.classList.add('is-knocked');
      delete photo.dataset.knocking;
      return Promise.resolve();
    }

    const nocrop = photo.hasAttribute('data-knock-nocrop');
    const rawProfile = photo.getAttribute('data-knock-profile') || 'default';
    const profile = rawProfile === 'leather' ? 'leather' : 'default';
    const maxEdge = maxEdgeFor(photo);
    const cacheKey = `${profile}:${maxEdge}:${nocrop ? 'nocrop:' : ''}${src}`;

    if (cache.has(cacheKey)) {
      reveal(photo, cache.get(cacheKey));
      return Promise.resolve();
    }

    if (photo.dataset.knocking === cacheKey) return Promise.resolve();
    photo.dataset.knocking = cacheKey;

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const hangTimer = setTimeout(() => {
        /* Never leave tiles at opacity:0 if canvas/CORS hangs */
        cache.set(cacheKey, src);
        reveal(photo, src);
        finish();
      }, 8000);

      const applyClear = (sourceImg) => {
        /* Defer canvas work to the next task so input/hover can paint first */
        setTimeout(() => {
          Promise.resolve()
            .then(() => clearWhiteBackground(sourceImg, { nocrop, profile, maxEdge }))
            .then((cleared) => {
              if (cleared) {
                cache.set(cacheKey, cleared);
                if (photo.dataset.knocking === cacheKey) {
                  reveal(photo, cleared);
                }
              } else {
                cache.set(cacheKey, src);
                reveal(photo, src);
              }
            })
            .catch((err) => {
              console.warn('[knock-white] failed', err);
              reveal(photo, src);
            })
            .finally(() => {
              clearTimeout(hangTimer);
              finish();
            });
        }, 0);
      };

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        applyClear(img);
      };
      img.onerror = function () {
        cache.set(cacheKey, src);
        reveal(photo, src);
        clearTimeout(hangTimer);
        finish();
      };
      img.src = src;
    });
  }

  function collectTargets(scope) {
    const root = scope || document;
    return [
      ...root.querySelectorAll(
        '.auracase-collections__phone-img[data-knock-white], .auracase-spotlight__phone-img[data-knock-white]'
      ),
    ];
  }

  function run(root) {
    const targets = collectTargets(root);
    if (!targets.length) return;

    /* Kick above-the-fold tiles immediately so skeleton → transparent is fast */
    targets.slice(0, 8).forEach((img, i) => enqueue(img, i < 3));

    /* Lazy: knock the rest as they approach the viewport */
    if (typeof IntersectionObserver === 'undefined') {
      targets.slice(8).forEach((img) => enqueue(img, false));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          enqueue(entry.target, true);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    targets.slice(8).forEach((img) => io.observe(img));
  }

  function scheduleRun(root) {
    const start = () => run(root);
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(start, { timeout: 1200 });
    } else {
      window.setTimeout(start, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleRun());
  } else {
    scheduleRun();
  }

  document.addEventListener('shopify:section:load', (event) => scheduleRun(event.target));

  if (typeof MutationObserver !== 'undefined') {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'src' && m.target.matches?.('img[data-knock-white]')) {
          if (!isHomeKnockTarget(m.target)) continue;
          if (m.target.dataset.knockRevealing) continue;
          const next = m.target.getAttribute('src') || '';
          if (next.startsWith('data:') || next.startsWith('blob:')) continue;
          delete m.target.dataset.knocking;
          m.target.classList.remove('is-knocked');
          enqueue(m.target, true);
        }
        if (m.type === 'childList') {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches?.('img[data-knock-white]')) enqueue(node, isHomeKnockTarget(node));
            else if (node.querySelectorAll) {
              [...node.querySelectorAll('img[data-knock-white]')].slice(0, 12).forEach((img) => {
                enqueue(img, isHomeKnockTarget(img));
              });
            }
          });
        }
      }
    });
    mo.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src'],
    });
  }
})();
